import { LiaDataElement } from '@liasoft/lit-ui';
import { html, nothing } from 'lit';
import { ago, dateOnly, number } from '../../shared/ui/format.js';
import { errorPanel, loadingPanel } from '../../shared/ui/templates.js';
import { breadcrumb } from '../../shared/ui/breadcrumb.js';
import {
   confirmIdentityLink, linkAccount, loginOptions, myAccounts, overview, pendingIdentityLink, releaseLock, startIdentityLink, switchAccount, unlinkIdentity, usage
} from '../api/developer.js';
import { PROFILE_PATH } from '../accounts.js';

/**
 * The developer's own page: who covdbg thinks they are, and what their personal use stands at.
 *
 * The sidebar's Overview at `/`, and "Your profile" under their name at `/profile`; the same page, and
 * only the trail differs.
 *
 * The account's one e-mail, read-only: an account is its e-mail and it never changes, so using another
 * address means adding another account (verified by a link we mail). Beside it the identities the account
 * can sign in with (GitHub: link another, unlink one) and the other accounts the same identity reaches
 * (switch to one, add one).
 *
 * And the personal lock: the one private repository personal use is tied to, with the way to release
 * it, whenever they like. A person who holds a seat somewhere needs no lock, and the page says so - but
 * a lock they took before the seat is still shown, with its button, because it is what personal use
 * falls back to the day the seat goes, and this is the one place it can be released.
 *
 * Everything else about the person is a page of its own underneath, reached from the menu under their
 * name: where they are signed in, and their own run log. Their teams are not the profile's subject, and
 * live in the sidebar.
 *
 * Everything here is one request - `GET /api/v1/seats/me/overview` - which is only ever about the
 * caller. There is no parameter on this page that could be pointed at somebody else.
 */
export class UserDashboard extends LiaDataElement
{
   static properties = {
      data: { state: true },
      runs: { state: true },
      notice: { state: true },
      busy: { state: true },
      accounts: { state: true },
      providers: { state: true },
      adding: { state: true },
      sent: { state: true },
      pending: { state: true }
   };

   constructor()
   {
      super();
      this.data = null;
      this.runs = null;
      this.notice = null;
      this.busy = false;

      // The other accounts of this sign-in, and the providers an identity can be linked from.
      this.accounts = [];
      this.providers = [];

      // Whether the address field for a new account is open; and, once its link is mailed, { email, userCode }.
      this.adding = false;
      this.sent = null;
      this.timer = null;

      // Where the provider callback left us after "Link GitHub".
      const params = new URLSearchParams(location.search);
      const identity = params.get('identity');

      if (identity && IDENTITY_NOTICES[identity])
      {
         this.notice = IDENTITY_NOTICES[identity];
      }

      // The callback links nothing: it hands back a ticket. The page shows what it would link (`pending`) and
      // this session spends the ticket only when the person clicks Link (D30).
      this.linkTicket = params.get('linkTicket');
      this.pending = null;
   }

   /**
    * Asks what the callback's ticket would link, without spending it. The ticket leaves the address bar first, so
    * a reload never offers it again; it is spent only by `confirmLink`, on the person's click.
    */
   async readLink(signal)
   {
      const ticket = this.linkTicket;

      this.linkTicket = null;

      if (!ticket)
      {
         return;
      }

      history.replaceState({}, '', PROFILE_PATH);

      try
      {
         this.pending = { ticket, ...(await pendingIdentityLink(ticket, signal)) };
      }
      catch (error)
      {
         this.notice = linkFailure(error);
      }
   }

   /** The person agreed: spend the ticket with this session, which the service accepts only from the one that started the flow. */
   async confirmLink()
   {
      const { ticket } = this.pending;

      this.busy = true;
      this.pending = null;

      try
      {
         await confirmIdentityLink(ticket);
         this.notice = IDENTITY_NOTICES.linked;
         await this.reload();
      }
      catch (error)
      {
         this.notice = linkFailure(error);
      }
      finally
      {
         this.busy = false;
      }
   }

   cancelLink()
   {
      this.pending = null;
      this.notice = { variant: 'info', message: 'Nothing was linked.' };
   }

   disconnectedCallback()
   {
      super.disconnectedCallback();
      this.stopWaiting();
   }

   stopWaiting()
   {
      if (this.timer)
      {
         clearTimeout(this.timer);
         this.timer = null;
      }
   }

   async load(signal)
   {
      // Usage beside the overview rather than after it, and never in its way: a telemetry database
      // that is down answers `available: false`, and a request that fails outright is the same thing.
      // The same goes for the accounts and the providers, which only decorate the identities card.
      await this.readLink(signal);

      const [data, runs, accounts, options] = await Promise.all([
         overview(signal),
         usage(30, signal).catch(() => null),
         myAccounts(signal).catch(() => []),
         loginOptions(signal).catch(() => ({ providers: [] }))
      ]);

      this.data = data;
      this.runs = runs?.available ? runs : null;
      this.accounts = accounts ?? [];
      this.providers = options?.providers ?? [];
   }

   /** Leaves for the provider; the callback links what it returns to this account and lands back here. */
   async linkIdentity(provider)
   {
      this.busy = true;
      this.notice = null;

      try
      {
         const { url } = await startIdentityLink(provider.id);

         this.go(url);
      }
      catch (error)
      {
         this.notice = { variant: 'danger', message: error.message };
         this.busy = false;
      }
   }

   /** A page load: the provider's page, or the exchange. Its own method so a test can watch where it goes. */
   go(url)
   {
      location.assign(url);
   }

   unlink(identity)
   {
      void this.act(
         () => unlinkIdentity(identity.id),
         'Unlinked. You can still sign in with a link by e-mail, and link it again whenever you like.');
   }

   /** Signs in to another account of this identity: a single-use code, spent on the exchange page. */
   async switchTo(account)
   {
      this.busy = true;
      this.notice = null;

      try
      {
         const { exchange } = await switchAccount(account.principalId);

         this.go(`/login/exchange?token=${encodeURIComponent(exchange)}&next=${encodeURIComponent(PROFILE_PATH)}`);
      }
      catch (error)
      {
         this.notice = { variant: 'danger', message: error.message };
         this.busy = false;
      }
   }

   /**
    * Mails the link that adds an account. The session is the proof of the identity (if it has one), the link of the address.
    * This tab does not poll for a session - it is signed in already - it watches for the account to appear, which it
    * only does for a session that came through a provider account (D32: a mailed-link session links nothing).
    */
   async addAccount(event)
   {
      event.preventDefault();

      const email = String(new FormData(event.target).get('email') ?? '').trim();

      if (!email)
      {
         return;
      }

      this.busy = true;
      this.notice = null;

      try
      {
         const started = await linkAccount({ email });

         this.sent = { email, userCode: started.userCode ?? '' };
         this.adding = false;
         this.waitForAccount(this.accounts.length);
      }
      catch (error)
      {
         this.notice = { variant: 'danger', message: error.message };
      }
      finally
      {
         this.busy = false;
      }
   }

   waitForAccount(known, attempts = 0)
   {
      this.stopWaiting();

      if (attempts > MAX_WAITS)
      {
         return;
      }

      this.timer = setTimeout(async () =>
      {
         try
         {
            const accounts = await myAccounts();

            if (accounts.length > known)
            {
               this.accounts = accounts;
               this.sent = null;
               this.notice = { variant: 'success', message: 'The account was added. You can switch to it below.' };
               return;
            }
         }
         catch
         {
            // Keep asking: the link may simply not have been opened yet.
         }

         this.waitForAccount(known, attempts + 1);
      }, WAIT_MS);
   }

   async act(run, describe)
   {
      this.busy = true;
      this.notice = null;

      try
      {
         await run();
         this.notice = { variant: 'success', message: describe };
         await this.reload();
      }
      catch (error)
      {
         this.notice = { variant: 'danger', message: error.message };
      }
      finally
      {
         this.busy = false;
      }
   }

   release()
   {
      void this.act(
         () => releaseLock(),
         'The lock is released. The next private repository you run covdbg in becomes the one.');
   }

   render()
   {
      const { displayName, primaryEmail, lastSeenAt, gitHub } = this.data ?? {};

      // Not the address again when it is already the heading, which it is for anybody who signed in
      // with a link and has no other name here.
      const subtitle = [
         primaryEmail === displayName ? null : primaryEmail,
         gitHub ? `GitHub ${gitHub}` : null,
         lastSeenAt ? `last run ${ago(lastSeenAt)}` : null
      ].filter(Boolean).join(' · ');

      return html`
         <lia-page
            title=${displayName || primaryEmail || 'Your profile'}
            icon="fa-solid fa-user"
            description=${subtitle}
         >
            <div class="d-flex flex-column gap-3">
               ${breadcrumb(location.pathname === PROFILE_PATH ? [{ label: 'Your profile' }] : [])}
               ${this.error ? errorPanel(this.error, () => this.reload()) : nothing}

               ${this.notice
                  ? html`<lia-alert
                       variant=${this.notice.variant}
                       message=${this.notice.message}
                       dismissible
                       @lia-dismiss=${() => { this.notice = null; }}
                    ></lia-alert>`
                  : nothing}

               ${this.pending ? this.pendingCard : nothing}

               ${!this.data && !this.error ? loadingPanel('Loading your profile') : nothing}

               ${this.data ? this.personalUse : nothing}

               ${this.runs ? this.usageCard : nothing}

               ${this.data ? this.emailCard : nothing}

               ${this.data ? this.identitiesCard : nothing}

               ${this.data ? this.accountsCard : nothing}
            </div>
         </lia-page>`;
   }

   /**
    * The person's own runs, as the client reported them. Only when the telemetry service answered:
    * a card that says zero because the service was down would be a lie.
    */
   get usageCard()
   {
      const { runs, lastSevenDays } = this.runs;

      return html`
         <lia-card title="Your runs" icon="fa-solid fa-gauge-high">
            <lia-stat-row
               .items=${[
                  { label: 'Last 7 days', value: number(lastSevenDays), icon: 'fa-solid fa-play', variant: 'off' },
                  { label: 'Last 30 days', value: number(runs), icon: 'fa-solid fa-calendar', variant: 'off' }
               ]}
               columns="2"
               class="d-block"
            ></lia-stat-row>
            <p class="small text-body-secondary mb-0 mt-3">
               Reported by covdbg after each run, and kept for 180 days. Set <code>COVDBG_TELEMETRY=off</code>,
               or <code>telemetry: false</code> under <code>settings</code> in <code>.covdbg.yaml</code>, and it stops.
            </p>
         </lia-card>`;
   }

   /**
    * Where personal use stands: the teams where a seat covers a covdbg login, and the personal team's
    * lock (one private repository, or none yet). A seat covers only the team it is in and a login is for
    * one team, so a seat never replaces the personal lock; both are said, and releasing the lock is a
    * click here.
    */
   get personalUse()
   {
      const { lock } = this.data;
      const teams = seatTeamNames(this.data);

      return html`
         <lia-card title="Personal use" icon="fa-solid fa-lock">
            ${teams.length
               ? html`
                  <p class="mb-2" data-seat-teams>
                     You hold a seat in ${teams.join(', ')}. It covers a covdbg login for ${teams.length === 1 ? 'that team' : 'those teams'}
                     only, and a login is for one team. Your personal team follows the personal lock below.
                  </p>`
               : nothing}
            ${lock
               ? html`
                     <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <span>
                           Your personal team is locked to
                           <code>${lock.remoteKey}</code>${lock.lockedAt ? ` since ${dateOnly(lock.lockedAt)}` : ''}.
                           Public repositories stay free.
                        </span>
                        <lia-action-bar
                           size="sm"
                           .actions=${[{
                              id: 'release',
                              label: 'Release the lock',
                              icon: 'fa-solid fa-lock-open',
                              variant: 'outline-secondary',
                              size: 'sm',
                              disabled: this.busy,
                              confirm: {
                                 title: 'Release the lock?',
                                 message: 'The next private repository you run covdbg in becomes the one.',
                                 confirmLabel: 'Release the lock'
                              }
                           }]}
                           @lia-action=${event => { if (event.detail?.action?.id === 'release') { this.release(); } }}
                        ></lia-action-bar>
                     </div>`
               : html`
                  <p class="mb-0">
                     Your personal team is not locked to a repository: public ones are free, and the first
                     private one you run covdbg in for it becomes yours.
                  </p>`}
         </lia-card>`;
   }

   /** What the provider just proved, and the question: nothing is linked until the person says yes (D30). */
   get pendingCard()
   {
      const { provider, issuer, subject, email } = this.pending;
      const what = `${providerLabel(provider)}${issuer ? ` (${issuer})` : ''} account ${subject}`;

      return html`
         <lia-card title="Link this identity?" icon="fa-solid fa-link">
            <p class="mb-3" data-pending-link>Link ${what} to ${email || 'this account'}?</p>
            <p class="small text-body-secondary">
               It will sign in to this account from now on. Only continue if you started this yourself, just now.
            </p>
            <div class="d-flex flex-wrap gap-2">
               <button type="button" class="btn btn-primary btn-sm" data-link-confirm ?disabled=${this.busy} @click=${() => this.confirmLink()}>Link</button>
               <button type="button" class="btn btn-outline-secondary btn-sm" data-link-cancel ?disabled=${this.busy} @click=${() => this.cancelLink()}>Cancel</button>
            </div>
         </lia-card>`;
   }

   /** The account's one e-mail. Read-only: it is the account, so another address is another account. */
   get emailCard()
   {
      return html`
         <lia-card title="E-mail" icon="fa-solid fa-envelope">
            <div class="d-flex flex-wrap align-items-center gap-2 mb-1" data-email>
               <i class="fa-solid fa-circle-check text-success" aria-hidden="true"></i>
               <span class="fw-semibold">${this.data.primaryEmail}</span>
            </div>
            <p class="small text-body-secondary mb-0">
               The e-mail is the account, so it does not change. To use another one, add an account.
               Commits are attributed to this address, and an invitation or a seat belongs to the account with that address.
            </p>
         </lia-card>`;
   }

   /** What this account signs in with, and the way to link another or take one away. */
   get identitiesCard()
   {
      const identities = this.data.identities ?? [];

      return html`
         <lia-card title="Identities" icon="fa-solid fa-fingerprint">
            ${identities.length
               ? html`
                  <ul class="list-group list-group-flush mb-3" data-identities>
                     ${identities.map(identity => html`
                        <li class="list-group-item px-0 d-flex flex-wrap align-items-center justify-content-between gap-2">
                           <span>
                              <i class=${`${IDENTITY_ICONS[identity.provider] ?? 'fa-solid fa-key'} me-2`} aria-hidden="true"></i>
                              <span class="fw-semibold">${providerLabel(identity.provider)}${identity.issuer ? ` (${identity.issuer})` : ''}</span>
                              <span class="text-body-secondary small ms-1">account ${identity.subject}${identity.linkedAt ? `, linked ${ago(identity.linkedAt)}` : ''}</span>
                           </span>
                           <lia-action-bar
                              size="sm"
                              .actions=${[{
                                 id: 'unlink',
                                 label: 'Unlink',
                                 icon: 'fa-solid fa-link-slash',
                                 variant: 'outline-secondary',
                                 size: 'sm',
                                 disabled: this.busy,
                                 confirm: {
                                    title: 'Unlink this identity?',
                                    message: 'It no longer signs in to this account. A link by e-mail still does.',
                                    confirmLabel: 'Unlink'
                                 }
                              }]}
                              @lia-action=${event => { if (event.detail?.action?.id === 'unlink') { this.unlink(identity); } }}
                           ></lia-action-bar>
                        </li>`)}
                  </ul>`
               : html`<p class="small text-body-secondary">Nothing is linked: this account signs in with a link by e-mail.</p>`}
            <div class="d-flex flex-wrap gap-2">
               ${this.providers.map(provider => html`
                  <button
                     type="button"
                     class="btn btn-outline-primary btn-sm"
                     data-link-provider=${provider.id}
                     ?disabled=${this.busy}
                     @click=${() => this.linkIdentity(provider)}
                  >
                     <i class=${`${IDENTITY_ICONS[provider.id] ?? 'fa-solid fa-key'} me-2`} aria-hidden="true"></i>Link ${provider.name}
                  </button>`)}
            </div>
         </lia-card>`;
   }

   /** The other accounts this sign-in reaches, and the way to add one. */
   get accountsCard()
   {
      const others = this.accounts.filter(account => !account.current);

      return html`
         <lia-card title="Other accounts" icon="fa-solid fa-users-between-lines">
            ${others.length
               ? html`
                  <ul class="list-group list-group-flush mb-3" data-accounts>
                     ${others.map(account => html`
                        <li class="list-group-item px-0 d-flex flex-wrap align-items-center justify-content-between gap-2">
                           <span class="fw-semibold">${account.email}</span>
                           <button
                              type="button"
                              class="btn btn-outline-secondary btn-sm"
                              ?disabled=${this.busy}
                              @click=${() => this.switchTo(account)}
                           >Switch to this account</button>
                        </li>`)}
                  </ul>`
               : html`<p class="small text-body-secondary">This sign-in reaches no other account.</p>`}

            ${this.sent
               ? html`
                  <div class="border rounded p-3" data-sent>
                     <p class="mb-1">We sent a link to <strong>${this.sent.email}</strong>.</p>
                     <p class="small text-body-secondary mb-2">Open it and enter this code there:</p>
                     <p class="h4 font-monospace user-select-all mb-2" data-user-code>${this.sent.userCode}</p>
                     <p class="small text-body-secondary mb-0">
                        <lia-spinner size="sm" class="me-2"></lia-spinner>Once the link is opened the account is created. It is listed here when this sign-in came through a provider account; a sign-in by mailed link reaches only its own account, so sign in to the new one with its own link.
                     </p>
                  </div>`
               : this.adding
                  ? html`
                     <form @submit=${event => this.addAccount(event)} data-add-form>
                        <label class="form-label" for="add-account-email">E-mail address of the new account</label>
                        <div class="input-group">
                           <input
                              id="add-account-email"
                              class="form-control"
                              type="email"
                              name="email"
                              autocomplete="email"
                              placeholder="you@example.com"
                              required
                              ?disabled=${this.busy}
                           >
                           <button class="btn btn-primary" type="submit" ?disabled=${this.busy}>Send me a link</button>
                           <button class="btn btn-outline-secondary" type="button" @click=${() => { this.adding = false; }}>Cancel</button>
                        </div>
                        <div class="form-text">We send a link to confirm the address. It becomes the new account's e-mail.</div>
                     </form>`
                  : html`
                     <button
                        type="button"
                        class="btn btn-outline-primary btn-sm"
                        data-add-account
                        ?disabled=${this.busy}
                        @click=${() => { this.adding = true; }}
                     >
                        <i class="fa-solid fa-plus me-2" aria-hidden="true"></i>Add an account
                     </button>`}
         </lia-card>`;
   }
}

const IDENTITY_ICONS = { github: 'fa-brands fa-github' };

const PROVIDER_LABELS = { github: 'GitHub' };

/** The names of the teams (not the personal one) where the person holds an active seat: what a seat covers. */
function seatTeamNames(data)
{
   return (data?.accounts ?? [])
      .filter(account => !account.personal && account.seat?.state === 'active')
      .map(account => account.name);
}

function providerLabel(provider)
{
   return PROVIDER_LABELS[provider] ?? provider;
}

/** What the provider callback says when it lands here after "Link GitHub", keyed by its `identity` parameter. */
/** What a refused or failed link says. 403: another session started it; 400: it is old or already used. */
function linkFailure(error)
{
   return {
      variant: 'danger',
      message: error.status === 403
         ? 'That sign-in was started from another session, so nothing was linked. Start again from the account you want to link it to.'
         : error.status === 400
            ? 'That link has expired or was already used. Nothing was linked; start again.'
            : error.message
   };
}

const IDENTITY_NOTICES = {
   linked: { variant: 'success', message: 'Linked. It signs in to this account from now on.' },
   denied: { variant: 'warning', message: 'The link was refused at the provider. Nothing was linked.' },
   failed: { variant: 'danger', message: 'The provider did not say who you are. Nothing was linked.' },
   state: { variant: 'warning', message: 'That link was not started in this browser, or took too long. Nothing was linked; start again.' }
};

/** How often, and how many times, the page asks whether the new account's link was opened (about ten minutes). */
const WAIT_MS = 3000;
const MAX_WAITS = 200;

customElements.define('user-dashboard', UserDashboard);
